import { useState, type FormEvent } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ActionSchema,
  AppAction,
  AppRoute,
} from '@agentselly/immodossier-shared';
import {
  CalendarIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  InformationCircleIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { fetchBackend } from '~/helper/fetchBackend';

interface Advisor {
  readonly name: string;
  readonly role: string;
  readonly phone?: string;
  readonly email?: string;
}

interface Props {
  readonly advisor?: Advisor | null;
  readonly bookUrl?: string | null;
  readonly hubspotMeetingLink?: string | null;
}

const MIN_LENGTH = 20;

export function V3ContactPage(props: Props) {
  const { advisor } = props;
  const bookingHref = props.bookUrl || props.hubspotMeetingLink;
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [
    message,
    setMessage,
  ] = useState('');
  const [
    invalid,
    setInvalid,
  ] = useState(false);
  const [
    status,
    setStatus,
  ] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');

  const initials = (advisor?.name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const bad = message.trim().length < MIN_LENGTH;
    setInvalid(bad);
    setStatus('idle');
    if (bad) {
      document.getElementById('cf-msg')?.focus();
      return;
    }
    const parsed = ActionSchema[AppAction.ContactForm].safeParse({
      message: message.trim(),
      contactType: 'email',
    });
    if (!parsed.success) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    try {
      const formData = new FormData();
      formData.append('message', parsed.data.message);
      formData.append('contactType', parsed.data.contactType);
      const response = await fetchBackend(`/action/${AppAction.ContactForm}`, {
        method: 'POST',
        headers: {},
        body: formData,
      });
      const result = response.ok ? await response.json() : null;
      if (!result?.success) {
        setStatus('error');
        return;
      }
      setMessage('');
      setStatus('ok');
      await queryClient.invalidateQueries({
        queryKey: [AppRoute.$LANG_$COMPANYID_$CONTACTID_CONTACT],
      });
    } catch {
      setStatus('error');
    }
  }

  const ln = (
    icon: React.ReactNode,
    label: string,
    value: string,
    href: string,
  ) => (
    <div className="v3-adv-ln">
      <span
        className="v3-adv-ln-icon"
        aria-hidden="true"
      >
        {icon}
      </span>
      <span style={{ minWidth: 0 }}>
        <small>{label}</small>
        <a href={href}>{value}</a>
      </span>
    </div>
  );

  return (
    <div className="v3-stack pb-8">
      <div
        className="v3-head"
        data-od-id="page-head"
      >
        <h1>Kontakt</h1>
        <p className="v3-mu">Wir beraten Sie persönlich zu Ihrer Immobilie.</p>
      </div>

      {advisor ? (
        <section
          className="v3-card"
          data-od-id="advisor"
          aria-label={advisor.role}
        >
          <div className="v3-adv-who">
            <span
              className="v3-av4"
              style={{ width: 56, height: 56, fontSize: 18 }}
              aria-hidden="true"
            >
              {initials}
            </span>
            <span style={{ minWidth: 0 }}>
              <small
                className="v3-mu"
                style={{ display: 'block', fontSize: 12, lineHeight: '16px' }}
              >
                {advisor.role}
              </small>
              <b>{advisor.name}</b>
            </span>
          </div>
          {advisor.phone
            ? ln(
                <PhoneIcon className="w-5 h-5" />,
                'Telefon',
                advisor.phone,
                `tel:${advisor.phone.replace(/ /g, '')}`,
              )
            : null}
          {advisor.email
            ? ln(
                <EnvelopeIcon className="w-5 h-5" />,
                'E-Mail',
                advisor.email,
                `mailto:${advisor.email}`,
              )
            : null}
        </section>
      ) : (
        <section
          className="v3-card"
          data-od-id="advisor-none"
        >
          <div className="v3-callout">
            <InformationCircleIcon
              className="w-[18px] h-[18px] flex-none"
              aria-hidden="true"
            />
            <span>
              Ihnen ist noch kein Betreuer zugeordnet. Schreiben Sie dem Team,
              wir weisen Ihnen jemanden zu. Eine Terminbuchung ist erst danach
              möglich.
            </span>
          </div>
        </section>
      )}

      <section
        className="v3-card"
        data-od-id="contact-book"
        aria-labelledby="h-bk"
      >
        <div
          className="v3-stack"
          style={{ gap: 4 }}
        >
          <h2 id="h-bk">Termin vereinbaren</h2>
          <p className="v3-mu v3-sm">
            {advisor
              ? `Wählen Sie einen passenden Zeitpunkt im Kalender von ${advisor.name}. Dauer 15 Minuten, per Telefon oder Video.`
              : 'Sobald Ihnen ein Betreuer zugeordnet ist, können Sie hier einen Termin buchen.'}
          </p>
        </div>
        <div
          className="v3-fbar"
          style={{ marginTop: 'auto', paddingTop: 16 }}
        >
          {advisor && bookingHref ? (
            <>
              <a
                id="bk-go"
                className="v3-btn pri"
                href={bookingHref}
                {...(props.bookUrl
                  ? {}
                  : { target: '_blank', rel: 'noopener noreferrer' })}
              >
                <CalendarIcon className="w-4 h-4" />
                Termin vereinbaren
              </a>
              {props.bookUrl ? null : (
                <span className="v3-src">Öffnet den HubSpot-Kalender</span>
              )}
            </>
          ) : (
            <button
              type="button"
              className="v3-btn pri"
              disabled
            >
              Termin vereinbaren
            </button>
          )}
        </div>
      </section>

      <form
        className="v3-card"
        id="cf"
        noValidate
        data-od-id="contact-form"
        aria-labelledby="h-msg"
        onSubmit={onSubmit}
      >
        <div
          className="v3-stack"
          style={{ gap: 4 }}
        >
          <h2 id="h-msg">Nachricht an uns</h2>
          <p className="v3-mu v3-sm">
            Wir antworten in der Regel innert eines Arbeitstags.
          </p>
        </div>
        <div className="v3-fgrid two">
          <div className="v3-fld">
            <label htmlFor="cf-n">Name</label>
            <input
              className="v3-inp"
              id="cf-n"
              value={user?.fullName ?? ''}
              autoComplete="name"
              readOnly
            />
          </div>
          <div className="v3-fld">
            <label htmlFor="cf-m">E-Mail</label>
            <input
              className="v3-inp"
              id="cf-m"
              type="email"
              value={user?.primaryEmailAddress?.emailAddress ?? ''}
              autoComplete="email"
              readOnly
            />
          </div>
        </div>
        {/* ponytail: the loader exposes no object list, so only the general inquiry exists; add objects once the contact loader returns them. */}
        <div className="v3-fld">
          <label htmlFor="cf-obj">Objekt</label>
          <select
            className="v3-inp"
            id="cf-obj"
          >
            <option>Allgemeine Anfrage</option>
          </select>
        </div>
        <div className="v3-fld">
          <label htmlFor="cf-msg">Ihre Nachricht</label>
          <textarea
            className="v3-inp"
            id="cf-msg"
            required
            minLength={MIN_LENGTH}
            style={{ minHeight: 160, padding: 12 }}
            aria-describedby="cf-e"
            aria-invalid={invalid}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <span
            className="v3-ferr"
            id="cf-e"
            hidden={!invalid}
          >
            Bitte schreiben Sie mindestens 20 Zeichen.
          </span>
        </div>
        <div className="v3-fbar">
          <button
            className="v3-btn pri"
            type="submit"
            disabled={status === 'sending'}
          >
            Nachricht senden
          </button>
          <span className="v3-src">
            <EnvelopeIcon
              className="w-[14px] h-[14px]"
              aria-hidden="true"
            />
            Landet als Anfrage in HubSpot
          </span>
        </div>
        {status === 'error' ? (
          <p
            className="v3-ferr"
            role="alert"
          >
            Die Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es
            erneut.
          </p>
        ) : null}
        <div
          className="v3-okbox"
          id="cf-ok"
          role="status"
          hidden={status !== 'ok'}
        >
          <CheckCircleIcon
            className="w-[18px] h-[18px] flex-none"
            aria-hidden="true"
          />
          <span>
            Danke, Ihre Nachricht ist angekommen. Sie werden innerhalb der
            nächsten Woche kontaktiert.
          </span>
        </div>
      </form>
    </div>
  );
}
